import {Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
    EnergyService, MeasuringPointAssignmentWithCustomerDto,
    MeasuringPointService,
    MeasuringPointState,
    MeasuringPointStateChangeSource,
    MeasuringPointType,
    SearchMeasuringPointResultDto,
    SearchCustomerDto, SortCriteriaDto, SortDirection,
    TariffDto, TariffService,
    CustomerCockpitDataDto
} from '../../../../_services/configuration-services';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {DatePipe} from '@angular/common';
import {ConsumptionData} from '../consumption-bar-chart/consumption-bar-chart.component';
import {environment} from '../../../../../environments/environment';
import {SearchMeasuringPointComponent} from '../../../../_shared/_components/search-measuring-point/search-measuring-point.component';
import {DateRange} from '../../../../_shared/_components/date-range-picker/date-range-picker.component';
import {DateUtils} from '../../../../_shared/date-utils';
import {TranslateUtils} from '../../../../_shared/translate-utils';
import {Dayjs} from "dayjs";
import dayjs from 'dayjs';

@Component({
    selector: 'app-customer-energy-cockpit',
    templateUrl: './cockpit-energy-customer.component.html',
    styleUrls: ['./cockpit-energy-customer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CockpitEnergyCustomerComponent implements OnInit, OnDestroy {

    @Input() serviceId!: string;
    @Input() customerId: string | null;
    @Input() measuringPoint: SearchMeasuringPointResultDto | null;
    @Input() isExploitant = false;
    @Input() tariffs: TariffDto[] | null;

    @ViewChild('searchMeasuringPoint') searchMeasuringPoint: SearchMeasuringPointComponent = null;

    dateFormat = DateUtils.dateFormat;

    customerMeasuringPointType = MeasuringPointType.Customer;
    MeasuringPointState = MeasuringPointState;
    MeasuringPointStateChangeSource = MeasuringPointStateChangeSource;

    canCommuteMeasuringPoint = false;

    dateRange: DateRange;
    maxDateFromForCustomer: Dayjs | null = null;
    minDateFromForCustomer: Dayjs | null = null;
    crtLang = TranslateUtils.defaultLanguage;
    customerIsSelected = false;

    currentAssignedFirstSort = new SortCriteriaDto({
        direction: SortDirection.Asc,
        property: 'Assignment'
    });

    cockpitData: CustomerCockpitDataDto;

    // Displayed values
    tariff: TariffDto | null = null;
    actualIntensity = null;
    consumptionData: ConsumptionData[] = [];
    measuringPointAssignment: MeasuringPointAssignmentWithCustomerDto | null = null;

    private langChangeSubscription$: Subscription;

    constructor(
        private readonly translateService: TranslateService,
        private readonly energyService: EnergyService,
        private readonly measuringPointService: MeasuringPointService,
        private readonly datePipe: DatePipe,
        private readonly tariffService: TariffService) {
    }

    ngOnInit(): void {
        this.setupLanguage();

        this.langChangeSubscription$ = this.translateService.onLangChange
            .subscribe(_ => {
                this.setupLanguage();
                window.location.reload();
            });

        if (!this.isExploitant) {
            this.customerIsSelected = true;

            this.energyService.getAvailablePeriodRangeForCurrentCustomerAndMeasuringPoint(this.measuringPoint.id).pipe()
                .subscribe(availableRangeForCustomer => {
                    this.maxDateFromForCustomer = dayjs(availableRangeForCustomer.to);
                    this.minDateFromForCustomer = dayjs(availableRangeForCustomer.from);
                });
        }

        this.canCommuteMeasuringPoint = environment.canCustomerCommuteInCockpit || this.isExploitant;
    }

    ngOnDestroy(): void {
        this.langChangeSubscription$?.unsubscribe();
    }

    async fetchCockpitData(): Promise<void> {
        if (this.measuringPoint) {

            let request: Observable<CustomerCockpitDataDto>;

            if (this.isExploitant) {
                // Only the date will be used by the backend (the hour of the day is ignored)
                request = this.energyService.getDataForCustomerCockpitWithCustomer(this.serviceId,
                    this.measuringPoint.id,
                    this.customerId,
                    this.formatDateForCockpitData(this.dateRange.start),
                    this.formatDateForCockpitData(this.dateRange.end));

            } else {
                // Update available daterange for measuring point
                const availableRangeForCustomer = await firstValueFrom(this.energyService.getAvailablePeriodRangeForCurrentCustomerAndMeasuringPoint(this.measuringPoint.id));

                this.maxDateFromForCustomer = dayjs(availableRangeForCustomer.to);
                this.minDateFromForCustomer = dayjs(availableRangeForCustomer.from);

                // Only the date will be used by the backend (the hour of the day is ignored)
                request = this.energyService.getDataForCustomerCockpit(this.measuringPoint.id,
                    this.formatDateForCockpitData(this.dateRange.start),
                    this.formatDateForCockpitData(this.dateRange.end));
            }

            this.cockpitData = await firstValueFrom(request);

            if (this.cockpitData.actualIntensity?.lastKnownValue != null && this.cockpitData.actualIntensity.lastKnownValue >= 0) {
                this.actualIntensity = this.cockpitData.actualIntensity.lastKnownValue.toFixed(2);

            } else {
                this.actualIntensity = '--';
            }

            this.consumptionData = this.cockpitData.consumptionMeasures.map<ConsumptionData>(c => ({
                value: c.value,
                date: c.dateTime
            }));
            this.measuringPointAssignment = this.cockpitData.assignment;

            if (this.consumptionData.length > 0) {
                this.dateRange.start = this.consumptionData[0].date;
                this.dateRange.end = this.consumptionData[this.consumptionData.length - 1].date;
            }

            await this.getTariffForCustomer(this.measuringPoint?.customer?.id);

        } else {
            this.cockpitData = null;
            this.consumptionData = [];
        }
    }

    async measuringPointSelected(measuringPoint: SearchMeasuringPointResultDto | null): Promise<void> {
        this.measuringPoint = measuringPoint;
        await this.fetchCockpitData();
    }

    async dateRangeChange(event: DateRange): Promise<void> {
        this.dateRange = event;
        await this.fetchCockpitData();
    }

    async changeMeasuringPointState(newState: boolean): Promise<void> {
        this.cockpitData.measuringPointStateChangeSource = MeasuringPointStateChangeSource.Unknown;
        // Exploitants must use the for-service endpoints ([AuthorizeExploitant]); the plain
        // variants are [AuthorizeRoles(Customer)] with an ownership check and would 403.
        if (newState) {
            await firstValueFrom(this.isExploitant
                ? this.measuringPointService.activateMeasuringPointForService(this.serviceId, this.measuringPoint.id)
                : this.measuringPointService.activateMeasuringPoint(this.measuringPoint.id));
        } else {
            await firstValueFrom(this.isExploitant
                ? this.measuringPointService.deactivateMeasuringPointForService(this.serviceId, this.measuringPoint.id)
                : this.measuringPointService.deactivateMeasuringPoint(this.measuringPoint.id));
        }
        await this.fetchCockpitData();
    }

    setupLanguage(): void {
        this.crtLang = this.translateService.currentLang;
    }

    formatDate(date: Date): string {
        return this.datePipe.transform(date, DateUtils.dateFormat);
    }

    customerSelected(customer: SearchCustomerDto): void {
        this.customerId = customer ? customer.id : null;
        this.customerIsSelected = !!customer;

        if (this.searchMeasuringPoint) {
            this.searchMeasuringPoint.clearInput();
            this.measuringPoint = null;
            this.cockpitData = null;
        }
    }

    private async getTariffForCustomer(customerId: string | null): Promise<void> {
        if (customerId) {
            this.tariff = await firstValueFrom(this.tariffService.getTariffForServiceAndCustomer(this.serviceId, customerId));
        } else {
            this.tariff = this.tariffs.find(t => t.defaultTariff);
        }
    }

    private formatDateForCockpitData(date: Date): string {
        return [`0${date.getDate()}`.substr(-2), `0${date.getMonth() + 1}`.substr(-2), `${date.getFullYear()}`].join('-');
    }
}
